// import node module libraries
import React, { useState, Fragment, useEffect, useRef } from "react";
import { Col, Row, Container } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";

// import custom components
import GKStepper from "components/elements/stepper/GKStepper";

// import sub components ( Steps )
import BasicInformation from "./steps/BasicInformation";
import CoursesMedia from "./steps/CoursesMedia";
import Curriculum from "./steps/Curriculum";
import Review from "./steps/Review";
import createCourseIcon from "../../../assets/images/New course.svg";
import CommonHeader from "layouts/dashboard/CommonHeader";
import { useCookies } from "react-cookie";
import { getCourseDetails } from "ServerAPI/serverAPI";
import { CircularProgress } from "@mui/material";

const AddNewCourse = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [allFiles, setAllFiles] = useState([]);
  const [cookies, setCookies] = useCookies();
  const [courseId, setCourseId] = useState(null);
  const [allSections, setAllSections] = useState([]);
  const [allContent, setAllContent] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [activeContent, setActiveContent] = useState(null);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [showContentPage, setShowContentPage] = useState(false);
  const [savedCourse, setSavedCourse] = useState({});
  const [editSavedCourse, setEditSavedCourse] = useState(false);
  const [courseDetailsLoading, setCourseDetailsLoading] = useState(false);
  const [courseDetails, setCourseDetails] = useState({
    courseId: courseId,
    courseTitle: "",
    courseLevel: "",
    courseDescription: "",
    courseDepartment: "",
    courseDomain: "",
    organizationId: cookies?.organizationId,
    coverImage: "",
    learningOutcome: [],
  });
  const [formData, setFormData] = useState({
    course_title: "Course Title",
    category_category: "React",
    courses_level: "Intermediate",
    course_description: "Ahmedabad",
  });
  const location = useLocation();

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };
  const next = () => {
    setCurrentStep(currentStep === 4 ? 1 : currentStep + 1);
  };
  const previous = () => {
    setCurrentStep(currentStep === 1 ? 1 : currentStep - 1);
  };

  const updateEditMode = (editMode) => {
    setEditMode(editMode);
  };

  const setStep = (step, isSectionEdit = false) => {
    setCurrentStep(step);
    setEditMode(isSectionEdit);
  };
  const showContentPageForEdit = () => {
    if (allSections.length === 0) return; // Don't show content page if no sections exist
    setStep(2, true);
    setShowContentPage(true);
  };

  const resetActiveQuiz = () => {
    setActiveQuiz(null);
  };

  const fetchCourseDetails = async (courseId) => {
    setCourseDetailsLoading(true);
    const response = await getCourseDetails(courseId, cookies.userId);
    if (response.status === 200) {
      setSavedCourse(response.data);
      setCourseDetails(() => {
        return {
          courseId: response.data?.courseId,
          courseTitle: response.data?.courseTitle,
          courseLevel: response.data?.courseLevel,
          courseDescription: response.data?.courseDescription,
          courseDepartment: response.data?.courseDepartment,
          courseDomain: response.data?.courseDomain,
          organizationId: response.data?.organizationId,
          coverImage: response.data?.coverImage,
          learningOutcome: response.data?.learningOutcome,
        };
      });

      // sort content based on contentNumber
      // Handle new API structure with subjectiveQuizzes array
      const validSections = (response.data?.sections || []).filter(
        (section) => section && Object.keys(section).length > 0 && section.sectionId
      );

      const formattedSectionForUpdateCourse = validSections.map(
        (section) => {
          // Get courseContent (existing structure)
          const courseContent = section.courseContent || [];
          
          // Get subjectiveQuizzes (new structure) if it exists
          const subjectiveQuizzes = section.subjectiveQuizzes || [];
          
          // Create a map to track content by contentId to avoid duplicates
          // Prioritize subjectiveQuizzes over courseContent if same contentId exists
          const contentMap = new Map();
          const contentWithoutId = []; // Store content without contentId
          
          // First, add all courseContent to the map
          courseContent.forEach((content) => {
            if (content.contentId) {
              contentMap.set(content.contentId, content);
            } else {
              // Store content without contentId separately
              contentWithoutId.push(content);
            }
          });
          
          // Then, add/override with subjectiveQuizzes (they have priority)
          subjectiveQuizzes.forEach((quiz) => {
            if (quiz.contentId) {
              // Override if exists in courseContent, or add new
              contentMap.set(quiz.contentId, quiz);
            } else {
              // If no contentId, add it to the array
              contentWithoutId.push(quiz);
            }
          });
          
          // Convert map to array, add content without contentId, and sort by contentNumber
          const mergedContent = [
            ...Array.from(contentMap.values()),
            ...contentWithoutId
          ].sort(
            (contentA, contentB) =>
              (contentA.contentNumber || 0) - (contentB.contentNumber || 0)
          ).map((content, index) => ({
            ...content,
            contentNumber: index + 1 // Ensure sequential contentNumber for matching
          }));
          
          console.log("fetchCourseDetails - Section processing:", {
            sectionTitle: section.sectionTitle,
            courseContentCount: courseContent.length,
            subjectiveQuizzesCount: subjectiveQuizzes.length,
            mergedContentCount: mergedContent.length,
            subjectiveQuizzes: subjectiveQuizzes
          });
          
          return {
            ...section,
            content: mergedContent,
          };
        }
      );

      // sort sections based on sectionNumber and push it to allSections state
      setAllSections(
        formattedSectionForUpdateCourse.sort(
          (sectionA, sectionB) =>
            sectionA.sectionNumber - sectionB.sectionNumber
        )
      );
    }
    setCourseDetailsLoading(false);
  };

  useEffect(() => {
    const editSavedCourse = location?.state?.editSavedCourse;
    const courseId = location?.state?.courseId;

    if (editSavedCourse) {
      setEditSavedCourse(true);
      fetchCourseDetails(courseId);
    }
  }, [location]);

  const steps = [
    {
      id: 1,
      title: "Course Details",
      content: (
        <BasicInformation
          data={formData}
          handleChange={handleChange}
          next={next}
          courseDetails={courseDetails}
          setCourseDetails={setCourseDetails}
          courseId={courseId}
          setCourseId={setCourseId}
          savedCourse={savedCourse}
          editSavedCourse={editSavedCourse}
        />
      ),
    },
    // {
    // 	id: 2,
    // 	title: 'Courses Media',
    // 	content: (
    // 		<CoursesMedia
    // 			data={formData}
    // 			handleChange={handleChange}
    // 			next={next}
    // 			previous={previous}
    // 		/>
    // 	)
    // },
    {
      id: 2,
      title: "Section",
      content: (
        <Curriculum
          data={formData}
          handleChange={handleChange}
          next={next}
          previous={previous}
          editMode={editMode}
          setEditMode={setEditMode}
          updateEditMode={updateEditMode}
          setStep={setStep}
          allFiles={allFiles}
          setAllFiles={setAllFiles}
          courseDetails={courseDetails}
          setCourseDetails={setCourseDetails}
          courseId={courseId}
          setCourseId={courseId}
          allSections={allSections}
          setAllSections={setAllSections}
          allContent={allContent}
          setAllContent={setAllContent}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          activeContent={activeContent}
          setActiveContent={setActiveContent}
          activeQuiz={activeQuiz}
          setActiveQuiz={setActiveQuiz}
          showContentPage={showContentPage}
          setShowContentPage={setShowContentPage}
          resetActiveQuiz={resetActiveQuiz}
          savedCourse={savedCourse}
          editSavedCourse={editSavedCourse}
        />
      ),
    },
    {
      id: 3,
      title: "Review",
      content: (
        <Review
          data={formData}
          handleChange={handleChange}
          next={next}
          previous={previous}
          setStep={setStep}
          allFiles={allFiles}
          setAllFiles={setAllFiles}
          courseDetails={courseDetails}
          setCourseDetails={setCourseDetails}
          courseId={courseId}
          setCourseId={courseId}
          allSections={allSections}
          setAllSections={setAllSections}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          showContentPageForEdit={showContentPageForEdit}
          activeContent={activeContent}
          setActiveContent={setActiveContent}
          setActiveQuiz={setActiveQuiz}
          savedCourse={savedCourse}
          editSavedCourse={editSavedCourse}
          allContent={allContent}
          setAllContent={setAllContent}
        />
      ),
    },
  ];

  useEffect(() => {
    if (location.state?.fromAddLecturePage) {
      setCurrentStep(2);
    }
    if (location.state?.fromAddLecturePage && location.state?.editMode) {
      setCurrentStep(3);
    }
    location.state = {};
  }, [location]);

  const sectionStyle = {
    height: "208px",
    background: "linear-gradient(93.26deg, #224B99 -10.17%, #3A4A6E 151.98%)",
  };

  return (
    <Fragment>
      <Row className="mb-4">
        <CommonHeader
          text={editMode ? "Manage Course" : "Manage course"}
          icon={createCourseIcon}
        />
      </Row>
      <Container className="w-75">
        <Row>
          <Col>
            {courseDetailsLoading ? (
              <div className="d-flex justify-content-center mt-2">
                <CircularProgress />
              </div>
            ) : (
              <GKStepper
                currentStep={currentStep}
                steps={steps}
                setStep={setStep}
              />
            )}
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default AddNewCourse;
